/**
 * h5/src/ws/eventBus.ts
 * 内部事件总线（零依赖，mitt 风格）
 *
 * 支持：
 * - on(type, handler)     订阅事件
 * - off(type, handler?)   取消订阅（handler 为 undefined 时清除该 type 所有订阅）
 * - once(type, handler)   只订阅一次
 * - emit(type, payload)   发布事件
 * - '*' 通配符            监听所有事件（接收完整 Envelope）
 * - clear()               清空所有订阅
 */

import type {
  EventBus,
  EventHandler,
  WildcardHandler,
  AnyEventType,
  SubscribableEventType,
  Envelope,
} from './types'

/**
 * 创建事件总线实例
 *
 * @example
 * ```ts
 * const bus = createEventBus()
 * bus.on('ad_updated', (payload) => { ... })
 * bus.on('*', (envelope) => { ... }) // 通配符
 * bus.emit('ad_updated', { ad_id: 1 })
 * ```
 */
export function createEventBus(): EventBus {
  // 普通事件订阅表（type → handlers）
  const _handlers = new Map<string, Set<EventHandler>>()
  // 通配符订阅表
  const _wildcardHandlers = new Set<WildcardHandler>()

  /**
   * 获取或初始化某 type 的 handler Set
   */
  function getOrCreate(type: string): Set<EventHandler> {
    let set = _handlers.get(type)
    if (!set) {
      set = new Set<EventHandler>()
      _handlers.set(type, set)
    }
    return set
  }

  /**
   * 实现 on 重载：分别处理通配符和普通类型
   */
  function on(type: string, handler: EventHandler | WildcardHandler): void {
    if (type === '*') {
      _wildcardHandlers.add(handler as WildcardHandler)
    } else {
      getOrCreate(type).add(handler as EventHandler)
    }
  }

  /**
   * 实现 off
   */
  function off(type: string, handler?: EventHandler | WildcardHandler): void {
    if (type === '*') {
      if (handler) {
        _wildcardHandlers.delete(handler as WildcardHandler)
      } else {
        _wildcardHandlers.clear()
      }
      return
    }
    const set = _handlers.get(type)
    if (!set) return
    if (handler) {
      set.delete(handler as EventHandler)
    } else {
      set.clear()
    }
  }

  /**
   * 实现 once：触发后自动取消
   */
  function once(type: AnyEventType, handler: EventHandler): void {
    const set = getOrCreate(type)
    const wrapper: EventHandler = (payload: unknown) => {
      handler(payload)
      set.delete(wrapper)
    }
    set.add(wrapper)
  }

  /**
   * 发布事件（普通类型订阅者）
   */
  function emit(type: AnyEventType, payload: unknown): void {
    const set = _handlers.get(type)
    if (set && set.size > 0) {
      // 拷贝快照防止回调中修改导致问题
      for (const h of [...set]) {
        h(payload)
      }
    }
  }

  /**
   * 向通配符订阅者发布完整信封
   */
  function emitEnvelope(envelope: Envelope): void {
    if (_wildcardHandlers.size === 0) return
    for (const h of [..._wildcardHandlers]) {
      h(envelope)
    }
  }

  /**
   * 清空所有订阅
   */
  function clear(): void {
    _handlers.clear()
    _wildcardHandlers.clear()
  }

  // 返回满足 EventBus 接口的对象
  // 使用函数重载兼容两种 on/off 签名
  const bus: EventBus = {
    on(type: AnyEventType | '*', handler: EventHandler | WildcardHandler): void {
      on(type, handler)
    },
    off(type: SubscribableEventType, handler?: EventHandler | WildcardHandler): void {
      off(type, handler)
    },
    once,
    emit,
    emitEnvelope,
    clear,
  }

  return bus
}
